import { useState } from 'react';
import { Activity, ChevronDown, ChevronRight, Info, RefreshCw } from 'lucide-react';
import { format, formatDistanceToNow, subDays } from 'date-fns';
import { toast } from 'sonner';
import {
  Alert,
  AlertContent,
  AlertDescription,
  AlertIcon,
  AlertTitle,
} from '@marine/ui/alert';
import { Card, CardContent } from '@marine/ui/card';
import { Badge } from '@marine/ui/badge';
import { Button } from '@marine/ui/button';
import { Skeleton } from '@marine/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@marine/ui/toggle-group';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@marine/ui/collapsible';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@marine/ui/table';
import { usePlatformAIUsage, useRefreshPlatformAIUsage } from './hooks/usePlatformAIUsage';

type WindowDays = 7 | 30 | 90;

const USD_FORMAT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});
const NUM_FORMAT = new Intl.NumberFormat('en-US');

/**
 * Platform → AI Settings → Usage tab (Phase 39.3, CTRL-15).
 *
 * Layout per D-15:
 *   1. Observability v1 browse-only banner (Alert)
 *   2. OpenRouter balance widget (Card)
 *   3. Time-window ToggleGroup (7 / 30 / 90 days)
 *   4. Provider cards grid (StatsRow pattern)
 *   5. Collapsible feature breakdown (Table)
 *
 * No charts, no custom date pickers, no CountingNumber animations, no toasts
 * on background refresh. Balance widget handles null gracefully (no-key and
 * HTTP-failure both render as "—" with inline captions).
 *
 * Freshness indicator uses `dataUpdatedAt` from TanStack Query (research §Q6)
 * so the "Last updated Xm ago" text reactively updates on each refetch without
 * component-level timestamp state.
 */
export function UsageTab() {
  const [days, setDays] = useState<WindowDays>(7);
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  const to = format(new Date(), 'yyyy-MM-dd');
  const from = format(subDays(new Date(), days - 1), 'yyyy-MM-dd');

  const usage = usePlatformAIUsage({ from, to });
  const breakdown = usePlatformAIUsage(
    { from, to, breakdown: 'feature' },
    { enabled: breakdownOpen },
  );

  const providers = usage.data?.providers ?? [];
  const balance = usage.data?.openrouter_balance ?? null;

  return (
    <div className="space-y-6">
      {/* Browse-only banner (D-16) */}
      <Alert variant="warning" appearance="light" icon="warning">
        <AlertIcon>
          <Info />
        </AlertIcon>
        <AlertContent>
          <AlertTitle>Observability v1 — browse-only</AlertTitle>
          <AlertDescription>
            Usage rollups refresh hourly. Quotas, alerts, and per-tenant drill-down
            are planned for a future phase.
          </AlertDescription>
        </AlertContent>
      </Alert>

      {/* OpenRouter balance widget */}
      <BalanceWidget
        balance={balance}
        isLoading={usage.isLoading}
      />

      {/* Time window + freshness row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ToggleGroup
          type="single"
          size="sm"
          variant="outline"
          value={String(days)}
          onValueChange={(value) => {
            if (value === '7' || value === '30' || value === '90') {
              setDays(Number(value) as WindowDays);
            }
          }}
        >
          <ToggleGroupItem value="7" aria-label="7 days">7d</ToggleGroupItem>
          <ToggleGroupItem value="30" aria-label="30 days">30d</ToggleGroupItem>
          <ToggleGroupItem value="90" aria-label="90 days">90d</ToggleGroupItem>
        </ToggleGroup>

        {usage.dataUpdatedAt > 0 ? (
          <span className="text-xs text-muted-foreground">
            Last updated{' '}
            {formatDistanceToNow(new Date(usage.dataUpdatedAt), { addSuffix: true })}
          </span>
        ) : null}
      </div>

      {/* Provider cards */}
      {usage.isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((k) => (
            <Skeleton key={k} className="h-32 w-full" />
          ))}
        </div>
      ) : providers.length === 0 ? (
        <Card>
          <CardContent className="flex items-center gap-3 py-8 text-sm text-muted-foreground">
            <Activity className="h-5 w-5" />
            <div>
              <div className="font-medium text-foreground">No usage data yet</div>
              <div className="text-xs">
                The hourly rollup populates this tab after the first successful AI call.
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {providers.map((p) => (
            <Card key={p.provider}>
              <CardContent className="flex flex-col gap-2 py-4">
                <Badge variant="secondary" className="w-fit capitalize">
                  {p.provider}
                </Badge>
                <div className="text-2xl font-semibold tracking-tight">
                  {USD_FORMAT.format(p.cost_estimate_usd)}
                </div>
                <div className="text-xs text-muted-foreground">
                  {NUM_FORMAT.format(p.total_tokens_in)} in /{' '}
                  {NUM_FORMAT.format(p.total_tokens_out)} out
                </div>
                <div className="text-xs text-muted-foreground">
                  {NUM_FORMAT.format(p.total_requests)} requests
                  {p.last_used_at
                    ? ` · last used ${formatDistanceToNow(new Date(p.last_used_at), { addSuffix: true })}`
                    : ''}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Feature breakdown (collapsed by default — D-15) */}
      <Collapsible open={breakdownOpen} onOpenChange={setBreakdownOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2">
            {breakdownOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            {breakdownOpen ? 'Hide feature breakdown' : 'Show feature breakdown'}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Feature</TableHead>
                    <TableHead className="text-right">Requests</TableHead>
                    <TableHead className="text-right">Tokens in</TableHead>
                    <TableHead className="text-right">Tokens out</TableHead>
                    <TableHead className="text-right">Cost (USD)</TableHead>
                    <TableHead>Last used</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {breakdown.isLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                        Loading feature breakdown…
                      </TableCell>
                    </TableRow>
                  ) : (breakdown.data?.features ?? []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                        No feature data in this window.
                      </TableCell>
                    </TableRow>
                  ) : (
                    (breakdown.data?.features ?? []).map((f) => (
                      <TableRow key={f.feature}>
                        <TableCell className="font-medium">{f.feature}</TableCell>
                        <TableCell className="text-right">
                          {NUM_FORMAT.format(f.total_requests)}
                        </TableCell>
                        <TableCell className="text-right">
                          {NUM_FORMAT.format(f.total_tokens_in)}
                        </TableCell>
                        <TableCell className="text-right">
                          {NUM_FORMAT.format(f.total_tokens_out)}
                        </TableCell>
                        <TableCell className="text-right">
                          {USD_FORMAT.format(f.cost_estimate_usd)}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {f.last_used_at
                            ? formatDistanceToNow(new Date(f.last_used_at), { addSuffix: true })
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

interface BalanceWidgetProps {
  balance: {
    credits_remaining_usd: number | null;
    usage_today_usd: number | null;
    last_fetched_at: string;
    stale: boolean;
  } | null;
  isLoading: boolean;
}

function BalanceWidget({ balance, isLoading }: BalanceWidgetProps) {
  const refresh = useRefreshPlatformAIUsage();

  const refreshButton = (
    <Button
      variant="ghost"
      size="icon"
      className="size-6 shrink-0"
      aria-label="Refresh OpenRouter balance"
      disabled={refresh.isPending}
      onClick={() =>
        refresh.mutate(undefined, {
          onError: () => toast.error('Could not refresh OpenRouter balance'),
        })
      }
    >
      <RefreshCw className={refresh.isPending ? 'animate-spin' : undefined} />
    </Button>
  );

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-6">
          <Skeleton className="h-10 w-40" />
        </CardContent>
      </Card>
    );
  }

  // D-21 — null balance (no key OR HTTP failure) renders "—" with inline
  // caption. The refresh button stays available so an admin who just fixed
  // the key can retry without waiting for the next poll.
  if (balance === null) {
    return (
      <Card>
        <CardContent className="flex flex-col gap-1 py-6">
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs text-muted-foreground">OpenRouter balance</div>
            {refreshButton}
          </div>
          <div className="text-3xl font-semibold tracking-tight text-muted-foreground">—</div>
          <div className="text-xs text-muted-foreground">Balance unavailable</div>
        </CardContent>
      </Card>
    );
  }

  const credits = balance.credits_remaining_usd;
  const usage = balance.usage_today_usd;

  return (
    <Card>
      <CardContent className="flex flex-col gap-1 py-6">
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs text-muted-foreground">OpenRouter balance</div>
          <div className="flex items-center gap-1">
            {balance.stale ? (
              <Badge variant="secondary" className="text-[10px]">
                stale — retrying
              </Badge>
            ) : null}
            {refreshButton}
          </div>
        </div>
        <div className="text-3xl font-semibold tracking-tight">
          {credits === null ? 'Unlimited' : USD_FORMAT.format(credits)}
        </div>
        <div className="text-xs text-muted-foreground">
          Used today: {usage === null ? '—' : USD_FORMAT.format(usage)}
        </div>
        <div className="text-[11px] text-muted-foreground">
          Fetched{' '}
          {formatDistanceToNow(new Date(balance.last_fetched_at), { addSuffix: true })}
        </div>
      </CardContent>
    </Card>
  );
}
